import { dashboard, login, register } from '@/routes';
import { type SharedData } from '@/types';
import { Head, Link, usePage } from '@inertiajs/react';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ThemeToggle } from '@/components/ui/theme-toggle';

export default function Welcome() {
    const { auth } = usePage<SharedData>().props;
    const [contactForm, setContactForm] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const handleContactSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // Handle contact form submission
        console.log('Contact form submitted:', contactForm);
        setContactForm({ name: '', email: '', subject: '', message: '' });
        alert('Thank you for your message! We will get back to you soon.');
    };

    const scrollToSection = (id: string) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
        setMobileMenuOpen(false); // Close mobile menu when navigating
    };

    // Initialize chart after component mounts
    useEffect(() => {
        const canvas = document.getElementById('revenueChart') as HTMLCanvasElement;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
                // Simple chart visualization
                ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                // Draw sample chart lines
                ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
                ctx.lineWidth = 3;
                ctx.beginPath();
                ctx.moveTo(50, 250);
                ctx.lineTo(150, 180);
                ctx.lineTo(250, 120);
                ctx.lineTo(350, 100);
                ctx.lineTo(450, 80);
                ctx.lineTo(550, 60);
                ctx.stroke();
            }
        }
    }, []);

    return (
        <>
            <Head>
                <title>SSA Accounts - Next-Gen Intelligent Accounting Platform</title>
                <meta name="description" content="Revolutionary accounting system featuring AI-powered receipt scanning, advanced approval workflows, multi-company management, and comprehensive audit trails." />
                <meta name="keywords" content="accounting software, AI receipt scanning, financial management, SSASoft, bookkeeping" />
                <meta property="og:title" content="SSA Accounts - Next-Gen Intelligent Accounting Platform" />
                <meta property="og:description" content="Revolutionary accounting system featuring AI-powered receipt scanning, advanced approval workflows, multi-company management, and comprehensive audit trails." />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
                <link rel="canonical" href="https://ssaaccounts.com" />
            </Head>
            <Head title="SSA Accounts - Next-Gen Intelligent Accounting Platform">
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
                <link
                    href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap"
                    rel="stylesheet"
                />
                <meta name="description" content="Revolutionary accounting system featuring AI-powered receipt scanning, advanced approval workflows, multi-company management, and comprehensive audit trails. Built with Laravel 12, React 19, and modern security standards." />
            </Head>

            <div className="min-h-screen font-['Inter'] antialiased text-[#0c1633] bg-white dark:bg-slate-900">
                {/* Header Navigation */}
                <header className="absolute top-0 left-0 right-0 z-[2]">
                    <div className="max-w-[1160px] mx-auto flex items-center justify-start gap-4 lg:gap-[72px] px-4 md:px-0 py-[26px] pb-[18px]">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center">
                                <img src="/images/logo.png.png" alt="SSA Accounts Logo" className="block h-8 lg:h-10" />
                            </div>
                            <div className="flex flex-col gap-0.5">
                                <span className="text-sm lg:text-[17.49px] font-extrabold uppercase text-center text-[#FF8C00]">
                                    SSA <span className="text-[#45A2F9]">ACCOUNTS</span>
                                </span>
                                <span className="text-[6px] lg:text-[8px] font-medium text-[#45A2F9]">
                                    Smart Solutions for Accurate Accounts
                                </span>
                            </div>
                        </div>

                        <nav className="flex items-center flex-1 text-[13px] font-medium">
                            <ul className="hidden md:flex items-center justify-center gap-6 lg:gap-10 flex-1 list-none m-0 p-0">
                                <li><button onClick={() => scrollToSection('features')} className="text-[#8c9ac3] hover:text-[#256dff] transition-colors duration-150">Features</button></li>
                                <li><button onClick={() => scrollToSection('services')} className="text-[#8c9ac3] hover:text-[#256dff] transition-colors duration-150">Services</button></li>
                                <li><button onClick={() => scrollToSection('about')} className="text-[#8c9ac3] hover:text-[#256dff] transition-colors duration-150">About</button></li>
                                <li><button onClick={() => scrollToSection('contact')} className="text-[#8c9ac3] hover:text-[#256dff] transition-colors duration-150">Contact</button></li>
                            </ul>

                            <div className="flex items-center gap-3 lg:gap-[18px] ml-auto">
                                {/*   <ThemeToggle variant="ghost" className="hover:bg-[#fe882d] hover:text-white transition-colors duration-300" />*/}

                                {/* Mobile Menu Button */}
                                <button
                                    className="md:hidden p-2 text-[#8c9ac3] hover:text-[#256dff] transition-colors"
                                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                    </svg>
                                </button>

                                {auth.user ? (
                                    <Link href={dashboard()} className="hidden md:block">
                                        <Button
                                            size="sm"
                                            className="h-8 lg:h-[42px] px-4 lg:px-[26px] rounded-[10px] text-xs lg:text-[13px] font-semibold bg-gradient-to-r from-[#1673ff] to-[#3b8cff] text-white shadow-[0_12px_30px_rgba(4,76,219,0.35)] hover:transform hover:-translate-y-px hover:shadow-[0_16px_40px_rgba(4,76,219,0.4)] transition-all duration-150"
                                        >
                                            Launch Dashboard
                                        </Button>
                                    </Link>
                                ) : (
                                    <div className="hidden md:flex items-center gap-3">
                                        <Link href={login()}>
                                            <button className="text-xs lg:text-[13px] font-medium text-[#256dff] hover:text-[#45A2F9] transition-colors duration-150">
                                                Sign In
                                            </button>
                                        </Link>
                                        <Link href={register()}>
                                            <Button
                                                size="sm"
                                                className="h-8 lg:h-[40px] px-4 lg:px-7 rounded-[10px] text-xs lg:text-[13px] font-semibold bg-gradient-to-r from-[#1673ff] to-[#3b8cff] text-white shadow-[0_12px_30px_rgba(4,76,219,0.35)] hover:transform hover:-translate-y-px hover:shadow-[0_16px_40px_rgba(4,76,219,0.4)] transition-all duration-150"
                                            >
                                                Start Free Trial
                                            </Button>
                                        </Link>
                                    </div>
                                )}
                            </div>
                        </nav>

                        {/* Mobile Menu */}
                        {mobileMenuOpen && (
                            <div className="md:hidden absolute top-full left-0 right-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-t border-slate-200 dark:border-slate-700 shadow-lg z-50">
                                <div className="px-4 py-4 space-y-4">
                                    <button
                                        onClick={() => scrollToSection('features')}
                                        className="block w-full text-left text-[#8c9ac3] hover:text-[#256dff] transition-colors duration-150 py-2"
                                    >
                                        Features
                                    </button>
                                    <button
                                        onClick={() => scrollToSection('services')}
                                        className="block w-full text-left text-[#8c9ac3] hover:text-[#256dff] transition-colors duration-150 py-2"
                                    >
                                        Services
                                    </button>
                                    <button
                                        onClick={() => scrollToSection('about')}
                                        className="block w-full text-left text-[#8c9ac3] hover:text-[#256dff] transition-colors duration-150 py-2"
                                    >
                                        About
                                    </button>
                                    <button
                                        onClick={() => scrollToSection('contact')}
                                        className="block w-full text-left text-[#8c9ac3] hover:text-[#256dff] transition-colors duration-150 py-2"
                                    >
                                        Contact
                                    </button>

                                    <div className="border-t border-slate-200 dark:border-slate-700 pt-4 space-y-3">
                                        {auth.user ? (
                                            <Link href={dashboard()} className="block">
                                                <Button
                                                    size="sm"
                                                    className="w-full h-[42px] px-4 rounded-[10px] text-[13px] font-semibold bg-gradient-to-r from-[#1673ff] to-[#3b8cff] text-white shadow-[0_12px_30px_rgba(4,76,219,0.35)]"
                                                >
                                                    Launch Dashboard
                                                </Button>
                                            </Link>
                                        ) : (
                                            <div className="space-y-3">
                                                <Link href={login()} className="block">
                                                    <button className="w-full text-center py-2 text-[13px] font-medium text-[#256dff] hover:text-[#45A2F9] transition-colors duration-150">
                                                        Sign In
                                                    </button>
                                                </Link>
                                                <Link href={register()} className="block">
                                                    <Button
                                                        size="sm"
                                                        className="w-full h-[40px] px-4 rounded-[10px] text-[13px] font-semibold bg-gradient-to-r from-[#1673ff] to-[#3b8cff] text-white shadow-[0_12px_30px_rgba(4,76,219,0.35)]"
                                                    >
                                                        Start Free Trial
                                                    </Button>
                                                </Link>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </header>

                <main>
                    {/* Hero Section */}

                    <section className="hero" aria-labelledby="hero-title" style={{
                        backgroundImage: "url('/images/hero.png')"
                    }}>
                        <div className="hero-overlay"></div>
                        <div className="container hero-inner">
                            <div className="hero-badge">
                                <span className="hero-badge-dot"></span>
                                <span className="hero-badge-text">
                                    Professional Accounting Solutions by SSASoft
                                </span>
                            </div>

                            <h1 id="hero-title" className="hero-title">
                                <span>Next-Gen Intelligent</span>
                                <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}><span className="account">Accounting</span> Platform</span>
                            </h1>

                            <p className="hero-subtitle">
                                Revolutionary accounting system featuring AI-powered receipt
                                scanning, advanced approval workflows, multi-company management,
                                and comprehensive audit trails. Built with Laravel 12, React 19,
                                and modern security standards.
                            </p>

                            <div className="hero-actions">
                                <button className="btn btn-primary">
                                    <span className="btn-label">Launch Dashboard</span>
                                    <span className="btn-icon-circle" aria-hidden="true">
                                        <span className="btn-icon-arrow"></span>
                                    </span>
                                </button>
                                <button className="btn btn-outline">Login to Your Books</button>
                            </div>
                        </div>
                    </section>


                    <section
                        className="dashboard-section charts"
                        aria-label="Financial overview dashboard preview"
                    >
                        <div className="container">
                            <div className="dashboard-card">
                                <div className="dashboard-browser-bar">
                                    <div className="browser-dots">
                                        <span className="dot dot-red"></span>
                                        <span className="dot dot-yellow"></span>
                                        <span className="dot dot-green"></span>
                                    </div>
                                    <div className="browser-address">
                                        app.ssasth.com/dashboard
                                    </div>
                                    <div className="browser-actions"></div>
                                </div>

                                <div className="dashboard-body">
                                    <aside className="dashboard-sidebar" aria-hidden="true">
                                        <div className="sidebar-pill sidebar-pill-primary"></div>
                                        <div className="sidebar-pill"></div>
                                        <div className="sidebar-pill"></div>
                                        <div className="sidebar-pill"></div>
                                        <div className="sidebar-pill"></div>
                                    </aside>

                                    <div className="dashboard-content">
                                        <div className="metrics-row">
                                            <article className="metric">
                                                <h2 className="metric-label">Total Revenue</h2>
                                                <p className="metric-value">$428,500.00</p>
                                                <p className="metric-change metric-change-positive">
                                                    + 12.5%
                                                </p>
                                            </article>

                                            <article className="metric">
                                                <h2 className="metric-label">Operating Expenses</h2>
                                                <p className="metric-value">$132,400.00</p>
                                                <p className="metric-change metric-change-positive">
                                                    + 4.2%
                                                </p>
                                            </article>

                                            <article className="metric">
                                                <h2 className="metric-label">Net Profit</h2>
                                                <p className="metric-value">$296,100.00</p>
                                                <p className="metric-change metric-change-positive">
                                                    + 18.7%
                                                </p>
                                            </article>
                                        </div>

                                        <div className="chart-wrapper">
                                            <div className="chart-header">
                                                <span className="chart-title">Performance Overview</span>
                                            </div>
                                            <div className="chart-canvas-wrap">
                                                <canvas
                                                    id="revenueChart"
                                                    width="960"
                                                    height="320"
                                                    aria-hidden="true"
                                                ></canvas>
                                            </div>
                                            <div className="chart-x-axis">
                                                <span>Feb</span>
                                                <span>Mar</span>
                                                <span>Apr</span>
                                                <span>May</span>
                                                <span>Jun</span>
                                                <span>Jul</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <aside className="payment-card" aria-label="Payment status">
                                    <div className="payment-icon">
                                        <span className="payment-check">✓</span>
                                    </div>
                                    <div className="payment-text">
                                        <span className="payment-label">Status</span>
                                        <span className="payment-status">Payment Approved</span>
                                    </div>
                                </aside>
                            </div>
                        </div>
                    </section>

                    { /* Revolutionary Features Section  */}
                    <section
                        id="features"
                        className="features-section container"
                        aria-label="Revolutionary accounting features"
                    >
                        <div>
                            <div className="features-shell">
                                <div className="features-badge">
                                    <span className="features-badge-dot"></span>
                                    <span className="features-badge-text">
                                        Professional Accounting Solutions by SSASoft
                                    </span>
                                </div>
                                <div className="features-header">
                                    <div className="features-heading">
                                        <h2 className="features-title">
                                            <span>Revolutionary</span>
                                            <span style={{ marginTop: '10px' }}>Accounting</span>
                                            <span style={{ marginTop: '10px' }}>Features</span>
                                        </h2>
                                    </div>
                                    <div className="features-intro">
                                        <p className="features-lede">
                                            Cutting-edge features that transform how you manage your
                                            financial operations.
                                        </p>
                                        <button className="btn btn-pill btn-features">
                                            Explore
                                        </button>
                                    </div>
                                </div>

                                <div className="features-grid" aria-label="Key platform features">
                                    <article className="feature-card feature-card-primary">
                                        <div className="feature-primary-image">
                                            <img
                                                src="/images/calculator.jpg"
                                                alt="AI receipt scanning on mobile"
                                            />
                                        </div>
                                        <div className="feature-primary-content">
                                            <div className="feature-primary-icon">
                                                <span className="feature-primary-icon-dot"></span>
                                            </div>
                                            <h3 className="feature-primary-title">AI Receipt Scanning</h3>
                                            <p className="feature-primary-text">
                                                Proprietary OCR models extract data with 99.9% accuracy.
                                                Auto-categorization and fraud detection built-in.
                                            </p>
                                        </div>
                                    </article>

                                    <article className="feature-card">
                                        <div className="feature-icon feature-icon-shield" aria-hidden="true">
                                            <svg className="feature-icon-svg" viewBox="0 0 24 24">
                                                <path d="M12 3l6 3v5c0 4-2.5 7-6 8-3.5-1-6-4-6-8V6z" />
                                            </svg>
                                        </div>
                                        <h3 className="feature-card-title">Audit-Ready Engine</h3>
                                        <p className="feature-card-text">
                                            Every transaction has a cryptographic paper trail, built for
                                            compliance from day one.
                                        </p>
                                    </article>

                                    <article className="feature-card">
                                        <div className="feature-icon feature-icon-grid" aria-hidden="true">
                                            <svg className="feature-icon-svg" viewBox="0 0 24 24">
                                                <rect x="5" y="5" width="4" height="4" rx="1" />
                                                <rect x="11" y="5" width="4" height="4" rx="1" />
                                                <rect x="5" y="11" width="4" height="4" rx="1" />
                                                <rect x="11" y="11" width="4" height="4" rx="1" />
                                            </svg>
                                        </div>
                                        <h3 className="feature-card-title">Multi-Entity Control</h3>
                                        <p className="feature-card-text">
                                            Manage multiple companies, currencies, and tax
                                            jurisdictions from a single unified view.
                                        </p>
                                    </article>

                                    <article className="feature-card">
                                        <div className="feature-icon feature-icon-user" aria-hidden="true">
                                            <svg className="feature-icon-svg" viewBox="0 0 24 24">
                                                <circle cx="12" cy="9" r="3" />
                                                <path d="M6 18c1.2-2 3-3 6-3s4.8 1 6 3" />
                                            </svg>
                                        </div>
                                        <h3 className="feature-card-title">Granular Permissions</h3>
                                        <p className="feature-card-text">
                                            Role-based access controls with advanced approval workflows
                                            for sensitive operations.
                                        </p>
                                    </article>

                                    <article className="feature-card">
                                        <div className="feature-icon feature-icon-lock" aria-hidden="true">
                                            <svg className="feature-icon-svg" viewBox="0 0 24 24">
                                                <rect x="6" y="10" width="12" height="9" rx="2" />
                                                <path d="M9 10V8a3 3 0 0 1 6 0v2" />
                                            </svg>
                                        </div>
                                        <h3 className="feature-card-title">Enterprise Security</h3>
                                        <p className="feature-card-text">
                                            Bank-grade encryption for all data at rest and in transit,
                                            with integrated hardware key support.
                                        </p>
                                    </article>

                                    <article className="feature-card">
                                        <div className="feature-icon feature-icon-chart" aria-hidden="true">
                                            <svg className="feature-icon-svg" viewBox="0 0 24 24">
                                                <path d="M5 17l4-4 3 3 5-7" />
                                                <path d="M5 5v12h14" />
                                            </svg>
                                        </div>
                                        <h3 className="feature-card-title">Real-time Analytics</h3>
                                        <p className="feature-card-text">
                                            Deep-dive into your financial health with interactive charts
                                            and automated reporting.
                                        </p>
                                    </article>
                                </div>
                            </div>
                        </div>
                    </section>

                    { /* Capabilities / High-Growth Teams Section  */}
                    <section
                        className="capabilities-section"
                        aria-label="Capabilities for high-growth teams"
                    >
                        <div className="container">
                            <div className="capabilities-badge">
                                <span className="capabilities-badge-dot"></span>
                                <span className="capabilities-badge-label">Capabilities</span>
                            </div>

                            <h2 className="capabilities-title">Built for High-Growth Teams</h2>

                            <p className="capabilities-subtitle">
                                Everything you need to handle finance at scale without the
                                administrative headache.
                            </p>

                            <div className="capabilities-grid">
                                <article className="cap-card">
                                    <div className="cap-icon cap-icon-automation" aria-hidden="true">
                                        <svg className="cap-icon-svg" viewBox="0 0 24 24">
                                            <circle cx="12" cy="12" r="4" />
                                            <path d="M12 4v2M12 18v2M4 12h2M18 12h2" />
                                        </svg>
                                    </div>
                                    <h3 className="cap-card-title">AI-Driven Automation</h3>
                                    <p className="cap-card-text">
                                        Leverage intelligent automation to streamline operations and
                                        eliminate manual workflows. Our AI adapts in real time,
                                        improving efficiency, accuracy, and decision-making at scale.
                                    </p>
                                </article>

                                <article className="cap-card">
                                    <div className="cap-icon cap-icon-insights" aria-hidden="true">
                                        <svg className="cap-icon-svg" viewBox="0 0 24 24">
                                            <rect x="4" y="11" width="3" height="7" rx="1" />
                                            <rect x="10" y="8" width="3" height="10" rx="1" />
                                            <rect x="16" y="6" width="3" height="12" rx="1" />
                                        </svg>
                                    </div>
                                    <h3 className="cap-card-title">Real-Time Financial Insights</h3>
                                    <p className="cap-card-text">
                                        Access live financial data with actionable insights when you
                                        need them most. Make faster, smarter decisions backed by
                                        real-time analytics and predictive intelligence.
                                    </p>
                                </article>

                                <article className="cap-card">
                                    <div className="cap-icon cap-icon-scale" aria-hidden="true">
                                        <svg className="cap-icon-svg" viewBox="0 0 24 24">
                                            <path d="M5 17L17 5" />
                                            <path d="M9 5h8v8" />
                                        </svg>
                                    </div>
                                    <h3 className="cap-card-title">
                                        Built to Scale with Your Business
                                    </h3>
                                    <p className="cap-card-text">
                                        Built with enterprise-grade security to protect your data at
                                        every layer. Fully compliant with industry standards, ensuring
                                        trust, privacy, and regulatory confidence.
                                    </p>
                                </article>

                                <article className="cap-card">
                                    <div className="cap-icon cap-icon-security" aria-hidden="true">
                                        <svg className="cap-icon-svg" viewBox="0 0 24 24">
                                            <path d="M12 3l6 3v5c0 4-2.5 7-6 8-3.5-1-6-4-6-8V6z" />
                                        </svg>
                                    </div>
                                    <h3 className="cap-card-title">
                                        Secure &amp; Compliant by Design
                                    </h3>
                                    <p className="cap-card-text">
                                        Designed to grow seamlessly as your business evolves. From
                                        startups to enterprises, our platform scales without
                                        compromising performance or reliability.
                                    </p>
                                </article>
                            </div>
                        </div>
                    </section>

                    { /* Our Vision Section  */}
                    <section className="vision-section" aria-label="Our vision">
                        <div className="container">
                            <div className="vision-layout">
                                <div className="vision-card">
                                    <div className="vision-row">
                                        <div className="vision-icon">
                                            <span className="vision-icon-glyph">💼</span>
                                        </div>
                                        <div className="vision-row-text">
                                            <h3 className="vision-row-title">International Experience</h3>
                                            <p className="vision-row-body">
                                                Decades of experience in international IT operations and
                                                solution delivery.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="vision-row">
                                        <div className="vision-icon">
                                            <span className="vision-icon-glyph">🛡️</span>
                                        </div>
                                        <div className="vision-row-text">
                                            <h3 className="vision-row-title">Progressive Security Solutions</h3>
                                            <p className="vision-row-body">
                                                Security services and management at fair prices with
                                                global compliance.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="vision-row vision-row-primary">
                                        <div className="vision-icon vision-icon-primary">
                                            <span className="vision-icon-glyph">⚙️</span>
                                        </div>
                                        <div className="vision-row-text">
                                            <h3 className="vision-row-title vision-row-title-primary">
                                                Integrated IT Solutions
                                            </h3>
                                            <p className="vision-row-body vision-row-body-primary">
                                                Comprehensive technology solutions from software
                                                development to digital branding.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="vision-copy">
                                    <div className="vision-badge">
                                        <span className="vision-badge-dot"></span>
                                        <span className="vision-badge-label">Our Vision</span>
                                    </div>
                                    <h2 className="vision-title">
                                        <span>We’re on a mission</span>
                                        <span>to automate</span>
                                        <span>financial truth.</span>
                                    </h2>
                                    <p className="vision-text">
                                        SSA Accounts started as a simple tool to manage receipts.
                                        Today, we’re the nervous system for thousands of growing
                                        businesses, providing the clarity they need to scale without
                                        fiction.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    { /* Workflow / Three Steps Section  */}
                    <section
                        className="workflow-section"
                        aria-label="Three steps to freedom workflow"
                    >
                        <div className="workflow-overlay"></div>
                        <div className="workflow-inner">
                            <div className="workflow-badge">
                                <span className="workflow-badge-dot"></span>
                                <span className="workflow-badge-label">Workflow</span>
                            </div>

                            <h2 className="workflow-title">Three Steps to Freedom</h2>

                            <div className="workflow-steps">
                                <article className="workflow-step">
                                    <div className="workflow-step-circle">
                                        <span className="workflow-step-number">01</span>
                                    </div>
                                    <h3 className="workflow-step-title">Upload Documents</h3>
                                    <p className="workflow-step-text">
                                        Send receipts via mobile app, email, or direct upload. Our AI
                                        handles the rest.
                                    </p>
                                </article>

                                <article className="workflow-step">
                                    <div className="workflow-step-circle">
                                        <span className="workflow-step-number">02</span>
                                    </div>
                                    <h3 className="workflow-step-title">Automate Approvals</h3>
                                    <p className="workflow-step-text">
                                        Workflows trigger automatically based on category, amount, or
                                        project.
                                    </p>
                                </article>

                                <article className="workflow-step">
                                    <div className="workflow-step-circle">
                                        <span className="workflow-step-number">03</span>
                                    </div>
                                    <h3 className="workflow-step-title">Real-Time Reports</h3>
                                    <p className="workflow-step-text">
                                        Access instant insights and export data for painless tax
                                        compliance.
                                    </p>
                                </article>
                            </div>
                        </div>
                    </section>

                    { /* Solutions by SSASoft Section  */}
                    <section
                        id="services"
                        className="solutions-section"
                        aria-label="Solutions by SSASoft"
                    >
                        <div className="solutions-wave"></div>
                        <div className="container solutions-inner">
                            <div className="solutions-badge">
                                <span className="solutions-badge-dot"></span>
                                <span className="solutions-badge-label">Our Expertise</span>
                            </div>

                            <h2 className="solutions-title">Solutions by SSASoft</h2>

                            <div className="solutions-grid">
                                <article className="solution-card">
                                    <div className="solution-icon">
                                        <span className="solution-icon-glyph">📄</span>
                                    </div>
                                    <h3 className="solution-title">Custom Accounting Software</h3>
                                    <p className="solution-text">
                                        Send receipts via mobile app, email, or direct upload. Our AI
                                        handles the rest.
                                    </p>
                                </article>

                                <article className="solution-card">
                                    <div className="solution-icon">
                                        <span className="solution-icon-glyph">📱</span>
                                    </div>
                                    <h3 className="solution-title">Mobile Accounting Apps</h3>
                                    <p className="solution-text">
                                        Manage your finances on the go, anywhere.
                                    </p>
                                </article>

                                <article className="solution-card">
                                    <div className="solution-icon">
                                        <span className="solution-icon-glyph">🔒</span>
                                    </div>
                                    <h3 className="solution-title">Financial Data Security</h3>
                                    <p className="solution-text">
                                        Top-tier protection for sensitive fiscal data.
                                    </p>
                                </article>

                                <article className="solution-card">
                                    <div className="solution-icon">
                                        <span className="solution-icon-glyph">💼</span>
                                    </div>
                                    <h3 className="solution-title">
                                        Business &amp; Financial Consulting
                                    </h3>
                                    <p className="solution-text">
                                        Strategic advice to grow your enterprise.
                                    </p>
                                </article>
                            </div>
                        </div>
                    </section>

                    { /* About SSASoft / Modern IT Solutions Section  */}
                    <section
                        id="about"
                        className="about-section"
                        aria-label="About SSASoft IT solutions"
                    >
                        <div className="container about-inner">
                            <div className="about-copy">
                                <div className="about-badge">
                                    <span className="about-badge-dot"></span>
                                    <span className="about-badge-label">About SSASOFT</span>
                                </div>

                                <h2 className="about-title">
                                    <span>Empowering Businesses</span>
                                    <span>Through Modern IT</span>
                                    <span>Solutions</span>
                                </h2>

                                <p className="about-text">
                                    SSASoft is an international IT solutions provider specializing
                                    in secure, scalable enterprise software. With years of
                                    experience serving a global client base, we bridge the gap
                                    between complex accounting needs and intuitive technology.
                                </p>

                                <div className="about-stats">
                                    <div className="about-stat">
                                        <div className="about-stat-value">500+</div>
                                        <div className="about-stat-label">Enterprise Clients</div>
                                    </div>

                                    <div className="about-stat">
                                        <div className="about-stat-value">99.9%</div>
                                        <div className="about-stat-label">Uptime SLA</div>
                                    </div>

                                    <div className="about-stat">
                                        <div className="about-stat-value">15+</div>
                                        <div className="about-stat-label">Countries Served</div>
                                    </div>

                                    <div className="about-stat">
                                        <div className="about-stat-value">24/7</div>
                                        <div className="about-stat-label">Support Center</div>
                                    </div>
                                </div>
                            </div>

                            <div className="about-media">
                                <div className="about-media-card">
                                    <img
                                        src="/images/robot.jpg"
                                        alt="Robotic assistant managing digital dashboards"
                                        className="about-media-image"
                                    />

                                    <div className="about-security-card">
                                        <div className="about-security-icon">
                                            <span className="about-security-check">✓</span>
                                        </div>
                                        <div className="about-security-text">
                                            <span className="about-security-title">Certified Security</span>
                                            <span className="about-security-subtitle">
                                                GDPR &amp; ISO Compliant
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    { /* CTA – Start Managing Accounts Section  */}
                    <section
                        className="cta-section"
                        aria-label="Start managing your accounts the smart way"
                    >
                        <div className="container">
                            <div className="cta-banner">
                                <h2 className="cta-title">
                                    Start Managing Your Accounts the Smart Way
                                </h2>

                                <div className="cta-actions">
                                    <Link href={register()} className="btn btn-pill cta-primary">
                                        Start Free Trial
                                    </Link>
                                    <button className="btn btn-pill cta-outline">
                                        Contact Sales
                                    </button>
                                </div>
                            </div>
                        </div>
                    </section>

                    { /* Contact / Ready to Transform Section  */}
                    <section
                        id="contact"
                        className="contact-section"
                        aria-label="Ready to transform your accounting"
                    >
                        <div className="container">
                            <div className="contact-card">
                                <div className="contact-copy">
                                    <div className="contact-badge">
                                        <span className="contact-badge-dot"></span>
                                        <span className="contact-badge-label">Let’s Connect</span>
                                    </div>

                                    <h2 className="contact-title">
                                        Ready to transform<br />
                                        your accounting?
                                    </h2>

                                    <p className="contact-text">
                                        Schedule a personalized demo with our solution architects
                                        today.
                                    </p>

                                    <ul className="contact-details">
                                        <li>
                                            <div className="contact-detail-icon">✉️</div>
                                            <div className="contact-detail-text">
                                                <span className="contact-detail-label">Email us</span>
                                                <a href="mailto:info@ssasoft.com" className="contact-detail-link"
                                                >info@ssasoft.com</a
                                                >
                                            </div>
                                        </li>
                                        <li>
                                            <div className="contact-detail-icon">🌐</div>
                                            <div className="contact-detail-text">
                                                <span className="contact-detail-label">Website</span>
                                                <a href="#" className="contact-detail-link">www.ssasoft.com</a>
                                            </div>
                                        </li>
                                        <li>
                                            <div className="contact-detail-icon">🔒</div>
                                            <div className="contact-detail-text">
                                                <span className="contact-detail-label">Security</span>
                                                <span className="contact-detail-link">
                                                    ISO Certified Security Solutions
                                                </span>
                                            </div>
                                        </li>
                                    </ul>
                                </div>

                                <form className="contact-form" onSubmit={handleContactSubmit}>
                                    <div className="contact-form-row contact-form-row-split">
                                        <div className="contact-field">
                                            <label className="contact-label" htmlFor="firstName">First Name</label>
                                            <input
                                                id="firstName"
                                                type="text"
                                                className="contact-input"
                                                name="firstName"
                                                value={contactForm.name.split(' ')[0] || ''}
                                                onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value + ' ' + (prev.name.split(' ')[1] || '') }))}
                                            />
                                        </div>
                                        <div className="contact-field">
                                            <label className="contact-label" htmlFor="lastName">Last Name</label>
                                            <input
                                                id="lastName"
                                                type="text"
                                                className="contact-input"
                                                name="lastName"
                                                value={contactForm.name.split(' ')[1] || ''}
                                                onChange={(e) => setContactForm(prev => ({ ...prev, name: (prev.name.split(' ')[0] || '') + ' ' + e.target.value }))}
                                            />
                                        </div>
                                    </div>

                                    <div className="contact-form-row">
                                        <div className="contact-field">
                                            <label className="contact-label" htmlFor="email">Email</label>
                                            <input
                                                id="email"
                                                type="email"
                                                className="contact-input"
                                                name="email"
                                                value={contactForm.email}
                                                onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="contact-form-row">
                                        <div className="contact-field">
                                            <label className="contact-label" htmlFor="message">
                                                Tell us about your needs
                                            </label>
                                            <textarea
                                                id="message"
                                                className="contact-input contact-textarea"
                                                name="message"
                                                rows={4}
                                                value={contactForm.message}
                                                onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                                                required
                                            ></textarea>
                                        </div>
                                    </div>

                                    <div className="contact-form-row contact-form-row-submit">
                                        <button type="submit" className="btn btn-pill contact-submit">
                                            Send Inquiry
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </section>

                    <footer className="site-footer" aria-label="Primary footer">
                        <div className="container">
                            <div className="footer-top">
                                <div className="footer-column footer-column-brand">
                                    <div className="footer-logo-row">
                                        <div className="footer-logo">
                                            <img
                                                src="/images/logo.png.png"
                                                alt="SSA Accounts logo"
                                            />
                                        </div>
                                        <div className="footer-brand-name">Accounts</div>
                                    </div>
                                    <p className="footer-brand-text">
                                        Developing intelligent financial systems for the next
                                        generation of businesses. Part of the SSASoft family of
                                        products.
                                    </p>
                                    <div className="footer-social">
                                        <a href="#" className="footer-social-link">Twitter</a>
                                        <a href="#" className="footer-social-link">LinkedIn</a>
                                        <a href="#" className="footer-social-link">GitHub</a>
                                    </div>
                                </div>

                                <div className="footer-column">
                                    <h3 className="footer-heading">Accounting features</h3>
                                    <ul className="footer-links">
                                        <li><a href="#" className="footer-link">Bookkeeping System</a></li>
                                        <li><a href="#" className="footer-link">Financial Reports</a></li>
                                        <li><a href="#" className="footer-link">Multi-Company</a></li>
                                        <li><a href="#" className="footer-link">Bank Security</a></li>
                                    </ul>
                                </div>

                                <div className="footer-column">
                                    <h3 className="footer-heading">Company</h3>
                                    <ul className="footer-links">
                                        <li><a href="#" className="footer-link">About SSASoft</a></li>
                                        <li><a href="#" className="footer-link">Parent Company</a></li>
                                        <li><a href="#" className="footer-link">Careers</a></li>
                                        <li><a href="#" className="footer-link">Contact</a></li>
                                    </ul>
                                </div>

                                <div className="footer-column">
                                    <h3 className="footer-heading">Legal &amp; Support</h3>
                                    <ul className="footer-links">
                                        <li><a href="#" className="footer-link">Privacy Policy</a></li>
                                        <li><a href="#" className="footer-link">Terms of Service</a></li>
                                        <li><a href="#" className="footer-link">Support Center</a></li>
                                        <li><a href="#" className="footer-link">Documentation</a></li>
                                    </ul>
                                </div>
                            </div>

                            <div className="footer-bottom">
                                <div className="footer-copy">
                                    © 2025 SSASoft Books. All rights reserved. Professional
                                    accounting solutions.
                                </div>
                                <div className="footer-powered">Powered by SSASoft</div>
                            </div>
                        </div>
                    </footer>

                </main>


            </div>
        </>
    );
}
